import styles from './ProductsList.module.scss';

import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bundle, ContentType, LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import { Button } from 'components/Button/Button';
import { Product } from 'components/Cart/InYourCart/Product/Product';
import { Grid } from 'components/Grid/Grid';
import { useCMSFilteredByDcsId } from 'hooks/useCMSByFilters';
import type { ICartDetailModel, IProductModel } from 'components/Cart/models';
import { ConfirmProductRemovalModal } from 'components/ConfirmProductRemovalModal/ConfirmProductRemovalModal';
import { ContentLinkV2 } from 'components/ContentLinkV2/ContentLinkV2';
import { RemoveCartItemModal } from 'components/RemoveCartItemModal/RemoveCartItemModal';
import { TextV2 } from 'components/TextV2/TextV2';
import { Link } from 'react-router-dom';
import { OrderType } from 'hooks/useDCSTypes';
import { CART_PRODUCTS_LIST_TEST_ID } from './ProductsList.const';
import { SoftwarePlan } from './SoftwarePlan/SoftwarePlan';
import { CloverCareBonusSection } from './CloverCareBonusSection/CloverCareBonusSection';
import { PRICING } from 'components/Pricing/utils/pricingHookTranslationStrings';
import { usePaymentsToggle } from 'components/Cart/OrderSummary/PaymentsToggle/usePaymentsToggle/usePaymentsToggle';
import { useCheckout } from 'components/Checkout/CheckoutProvider';
import {
  getCartProductsAndAccessories,
  isPayMonthlyAndCartHasProducts,
  transformCartProductsToProductObject,
} from 'components/Cart/utils/getCartProductsAndAccessoriesUtils';
import cx from 'classnames';
import { getTotalSubscriptionMonthlyHardwarePrice } from 'components/Cart/InYourCart/utils/getTotalSubscriptionMonthlyPrice';
import { CART_PRODUCT_IDS } from '../Cart.const';
import {
  generateCartAccessories,
  generateHeading,
  getSoftwarePlanMonthlyPrice,
} from './utils/productsListUtils';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

interface ICartProductsListProps {
  cartData: ICartDetailModel;
}

export const ProductsList: FC<ICartProductsListProps> = ({ cartData }) => {
  const { cartMetadata, orderType } = cartData || {};
  const { checkout, cart } = useCartContext();
  const { isPayMonthly } = usePaymentsToggle();
  const { selectedPurchaseType } = useCheckout();

  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [lastRemovedProduct, setLastRemovedProduct] = useState<IProductModel | null>();

  const { removeProductById, clearConflicts, productsWithRemovalConflict } = useCartContext();
  const hasRemovalConflicts = Boolean(productsWithRemovalConflict?.length);
  const [isConfirmRemovalModalOpen, setIsConfirmRemovalModalOpen] = useState(hasRemovalConflicts);

  const softwarePlanMonthlyPrice = getSoftwarePlanMonthlyPrice(
    checkout.installmentPlans,
    selectedPurchaseType,
  );

  const {
    payMonthlyCartProducts,
    allCartProducts,
    payInFullCartProducts,
    payInFullCartAccessories,
    payMonthlyCartAccessories,
    allCartAccessories,
    payMonthlyProductsAndAccessories,
  } = getCartProductsAndAccessories(cart);

  const tryRemoveProductById = (product: IProductModel) => {
    removeProductById(product.id);
    setLastRemovedProduct(product);
  };

  /**
   * if `PayMonthly payment` type is selected - get pay monthly cart products
   * if `PayInFull payment (!PayMonthly )` type is selected  - get all cart products + accessoires
   */
  const cartHasPayMonthlyProducts = isPayMonthlyAndCartHasProducts(isPayMonthly, payMonthlyCartProducts);
  const getCartProductsBasedOnPaymentType = cartHasPayMonthlyProducts
    ? payMonthlyCartProducts
    : allCartProducts;
  const cartProducts = transformCartProductsToProductObject(getCartProductsBasedOnPaymentType);
  const getAllCartAccessories = generateCartAccessories(allCartAccessories, tryRemoveProductById);
  const getPayMonthlyCartAccessories = generateCartAccessories(
    payMonthlyCartAccessories,
    tryRemoveProductById,
  );
  const getCartListByPaymentType = (
    cartHasPayMonthlyProducts
      ? [...cartProducts, ...getPayMonthlyCartAccessories]
      : [...cartProducts, ...getAllCartAccessories]
  ).filter(product => product.product?.id !== CART_PRODUCT_IDS.NO_HARDWARE);

  /**
   * if `PayMonthly payment` type is selected - get pay in full cart products + accessories
   */
  const getPayInFullCartAccessories = generateCartAccessories(payInFullCartAccessories, tryRemoveProductById);
  const getPayInFullCartListIfPayMonthlySelected =
    transformCartProductsToProductObject(payInFullCartProducts);
  const cartListWithPayInFullProductsAndAccessories = [
    ...getPayInFullCartListIfPayMonthlySelected,
    ...getPayInFullCartAccessories,
  ];
  const isPayMonthlyAndCartHasPayInFullProductsAndAccessories = isPayMonthlyAndCartHasProducts(
    isPayMonthly,
    cartListWithPayInFullProductsAndAccessories,
  );

  const link = useCMSFilteredByDcsId<Bundle>(ContentType.BUNDLE, cartMetadata?.id);

  useEffect(() => {
    setIsConfirmRemovalModalOpen(hasRemovalConflicts);
    if (!hasRemovalConflicts) {
      setLastRemovedProduct(null);
    }
  }, [productsWithRemovalConflict]);

  useEffect(() => {
    if (!isConfirmRemovalModalOpen && hasRemovalConflicts) {
      clearConflicts();
    }
  }, [isConfirmRemovalModalOpen, hasRemovalConflicts]);

  const cartTitleLink = cartData.cartMetadata?.pricingPageLink;
  const heading = generateHeading(cartMetadata, t);

  const subscriptionMonthlyHardwarePrice = getTotalSubscriptionMonthlyHardwarePrice(
    selectedPurchaseType,
    checkout,
  );

  const renderSoftwareMonthlyPrice = () => {
    return (
      isPayMonthly && (
        <TextV2
          className={styles.softwareMonthlyPrice}
          tagName="h4"
          variant="h4-bold"
          value={
            softwarePlanMonthlyPrice
              ? t(PRICING.STRING_MONTHLY_PRICE, {
                  price: subscriptionMonthlyHardwarePrice,
                })
              : undefined
          }
        />
      )
    );
  };

  const renderTitle = () => {
    if (cartTitleLink?.title) {
      return (
        <ContentLinkV2
          entry={link?.data?.pricingPageLink}
          hideTitle
          data-testid={CART_PRODUCTS_LIST_TEST_ID.cartLink}
        >
          <div className={styles.titleAndMonthlyPriceWrapper}>
            <TextV2
              value={heading}
              tagName="h4"
              variant="h4-medium"
              className={styles.cartProductListTitle}
            />
            {renderSoftwareMonthlyPrice()}
          </div>
        </ContentLinkV2>
      );
    }

    if (orderType === OrderType.BYO) {
      return (
        <Link to="/build-your-own?step=BUSINESS_OFFERING">
          <div className={styles.titleAndMonthlyPriceWrapper}>
            <TextV2 value={heading} tagName="h4" variant="h4-bold" />
            {renderSoftwareMonthlyPrice()}
          </div>
        </Link>
      );
    }

    return (
      <TextV2
        value={heading}
        tagName="h4"
        variant="h4-bold"
        className={styles.cartProductListTitle}
        data-testid={CART_PRODUCTS_LIST_TEST_ID.cartHeading}
      />
    );
  };

  const renderPayInFullItemsTitle = () => {
    return (
      <>
        <TextV2
          value={t('cart-pay-in-full-title')}
          tagName="h5"
          variant="h5-medium"
          className={cx(styles.cartProductListTitle, styles.byoPayInFullTitle)}
        />
        <TextV2
          value={t('cart-pay-in-full-sub-title')}
          tagName="h5"
          variant="h5"
          className={styles.cartProductListTitle}
        />
      </>
    );
  };

  const renderCartProducts = (products: { product: IProductModel }[]) => {
    return (
      <Grid>
        {products.map(item => (
          <Product productData={item} key={item.product.name} />
        ))}
        {isOpen && (
          <RemoveCartItemModal modalState={[isOpen, () => setIsOpen(!isOpen)]} systemName={heading} />
        )}
        {!!productsWithRemovalConflict?.length && lastRemovedProduct && (
          <ConfirmProductRemovalModal
            modalState={[
              isConfirmRemovalModalOpen,
              () => setIsConfirmRemovalModalOpen(!isConfirmRemovalModalOpen),
            ]}
            removedProduct={lastRemovedProduct}
            onConfirm={product => tryRemoveProductById(product)}
            products={productsWithRemovalConflict}
          />
        )}
      </Grid>
    );
  };

  return (
    <>
      <Grid xs={12} md={12} item>
        <div className={styles.titleContainer}>
          <TextV2 value={t('cart-page.title')} variant="h4" tagName="h4" className={styles.mainTitle} />
          <Button
            variant={LinkVariant.TEXT}
            id="empty-cart-button"
            aria-label={t('empty-cart')}
            onClick={() => setIsOpen(!isOpen)}
            className={styles.emptyCart}
            data-testid={CART_PRODUCTS_LIST_TEST_ID.emptyCart}
          >
            {t('cart-page.empty-cart')}
          </Button>
        </div>
        <Grid md={12} className={styles.cartProductList}>
          {renderTitle()}
          {renderCartProducts(getCartListByPaymentType)}
          <SoftwarePlan cartData={cartData} />
          <CloverCareBonusSection />
        </Grid>
      </Grid>

      {isPayMonthlyAndCartHasPayInFullProductsAndAccessories &&
        payMonthlyProductsAndAccessories.length > 0 && (
          <Grid xs={12} md={12} item className={styles.paddingTop}>
            <Grid md={12} className={styles.cartProductList}>
              {renderPayInFullItemsTitle()}
              {renderCartProducts(cartListWithPayInFullProductsAndAccessories)}
            </Grid>
          </Grid>
        )}
    </>
  );
};

ProductsList.displayName = 'ProductsList';
